import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { ContentNugget, PriorityPlaylist } from '@backrs_ui/common-ui';

import { PriorityTask } from './priorities.types';
import type { RootState } from '../store';

export type TaskDialogConfig =
  | {
      type: 'NEW_TASK';
      playlist: PriorityPlaylist | null;
      shouldAutoOpenPlaylistDrawer: boolean;
      preloadedTaskInfo?: Partial<PriorityTask>;
    }
  | {
      type: 'EDIT_TASK';
      task: PriorityTask;
      shouldAutoOpenPlaylistDrawer: boolean;
      selectedPlaylist?: PriorityPlaylist | null;
    }
  | { type: 'NEW_PLAYLIST'; preLoadedTitle?: string }
  | { type: 'EDIT_PLAYLIST'; playlist: PriorityPlaylist }
  | null;

type PrioritiesState = Readonly<{
  playlistDrawerStep:
    | {
        step: 'LIST';
      }
    | {
        step: 'VIEW';
        playlistId: number;
        isSuggestedPlaylist: boolean;
      }
    | null;

  dialog: TaskDialogConfig;

  // Allows opening a secondary dialog (e.g., Playlist) alongside Task dialog
  secondaryDialog:
    | { type: 'NEW_PLAYLIST'; preLoadedTitle?: string }
    | { type: 'EDIT_PLAYLIST'; playlist: PriorityPlaylist }
    | null;

  nuggetToAdd: ContentNugget | null;
}>;

const initialState: PrioritiesState = {
  playlistDrawerStep: null,
  dialog: null,
  secondaryDialog: null,
  nuggetToAdd: null,
};

export const prioritiesSlice = createSlice({
  name: 'priorities',
  initialState,
  reducers: {
    setNuggetToAdd: (state, action: PayloadAction<ContentNugget | null>) => {
      state.nuggetToAdd = action.payload;
    },
    openPlaylistListStep: (state) => {
      state.playlistDrawerStep = { step: 'LIST' };
    },
    openPlaylistViewStep: (state, action: PayloadAction<PriorityPlaylist | null>) => {
      if (action.payload) {
        state.playlistDrawerStep = {
          step: 'VIEW',
          playlistId: action.payload.id,
          isSuggestedPlaylist: false,
        };
      } else {
        state.playlistDrawerStep = null;
      }
    },
    openSuggestedPlaylistViewStep: (state, action: PayloadAction<PriorityPlaylist | null>) => {
      if (action.payload) {
        state.playlistDrawerStep = {
          step: 'VIEW',
          playlistId: action.payload.id,
          isSuggestedPlaylist: true,
        };
      } else {
        state.playlistDrawerStep = null;
      }
    },
    closePlaylistDrawer: (state) => {
      state.playlistDrawerStep = null;
    },

    openNewTaskDialog: (
      state,
      action: PayloadAction<{
        playlist: PriorityPlaylist | null;
        shouldAutoOpenPlaylistDrawer?: boolean;
        preloadedTaskInfo?: Partial<PriorityTask>;
      }>,
    ) => {
      state.dialog = {
        type: 'NEW_TASK',
        playlist: action.payload.playlist,
        shouldAutoOpenPlaylistDrawer: action.payload.shouldAutoOpenPlaylistDrawer ?? false,
        preloadedTaskInfo: action.payload.preloadedTaskInfo || undefined,
      };
    },
    openEditTaskDialog: (
      state,
      action: PayloadAction<{ task: PriorityTask; shouldAutoOpenPlaylistDrawer?: boolean }>,
    ) => {
      state.dialog = {
        type: 'EDIT_TASK',
        task: action.payload.task,
        shouldAutoOpenPlaylistDrawer: action.payload.shouldAutoOpenPlaylistDrawer ?? false,
      };
    },
    openNewPlaylistDialog: (
      state,
      action: PayloadAction<{ preLoadedTitle: string } | undefined>,
    ) => {
      state.dialog = {
        type: 'NEW_PLAYLIST',
        preLoadedTitle: action?.payload?.preLoadedTitle || '',
      };
    },
    openEditPlaylistDialog: (state, action: PayloadAction<PriorityPlaylist>) => {
      state.dialog = { type: 'EDIT_PLAYLIST', playlist: action.payload };
    },
    closeDialog: (state) => {
      state.dialog = null;
    },
    // Update selected playlist inside the NEW_TASK dialog without closing it
    setSelectedPlaylistForNewTaskDialog: (state, action: PayloadAction<PriorityPlaylist>) => {
      if (state.dialog?.type === 'NEW_TASK') {
        state.dialog = {
          ...state.dialog,
          playlist: action.payload,
        };
      }
    },
    // Update selected playlist inside the EDIT_TASK dialog without closing it
    setSelectedPlaylistForEditTaskDialog: (state, action: PayloadAction<PriorityPlaylist>) => {
      if (state.dialog?.type === 'EDIT_TASK') {
        state.dialog = {
          ...state.dialog,
          selectedPlaylist: action.payload,
        };
      }
    },
    // Secondary dialog controls to allow opening Playlist dialog alongside Task dialog
    openSecondaryNewPlaylistDialog: (
      state,
      action: PayloadAction<{ preLoadedTitle: string } | undefined>,
    ) => {
      state.secondaryDialog = {
        type: 'NEW_PLAYLIST',
        preLoadedTitle: action?.payload?.preLoadedTitle || '',
      };
    },
    openSecondaryEditPlaylistDialog: (state, action: PayloadAction<PriorityPlaylist>) => {
      state.secondaryDialog = { type: 'EDIT_PLAYLIST', playlist: action.payload };
    },
    closeSecondaryDialog: (state) => {
      state.secondaryDialog = null;
    },
  },
});

export const {
  setNuggetToAdd,
  openPlaylistListStep,
  openPlaylistViewStep,
  openSuggestedPlaylistViewStep,
  closePlaylistDrawer,
  openNewTaskDialog,
  openNewPlaylistDialog,
  closeDialog,
  openEditTaskDialog,
  openEditPlaylistDialog,
  setSelectedPlaylistForNewTaskDialog,
  setSelectedPlaylistForEditTaskDialog,
  openSecondaryNewPlaylistDialog,
  openSecondaryEditPlaylistDialog,
  closeSecondaryDialog,
} = prioritiesSlice.actions;

export const selectPlaylistDrawerStep = (state: RootState) => state.priorities.playlistDrawerStep;

export const selectIsPlaylistsDrawerOpen = (state: RootState) =>
  state.priorities.playlistDrawerStep !== null;

export const selectCurrentPlaylistId = (state: RootState) =>
  state.priorities.playlistDrawerStep?.step === 'VIEW'
    ? state.priorities.playlistDrawerStep.playlistId
    : null;

export const selectIsCurrentPlaylistSuggested = (state: RootState) =>
  state.priorities.playlistDrawerStep?.step === 'VIEW'
    ? state.priorities.playlistDrawerStep.isSuggestedPlaylist
    : false;

export const selectIsNewTaskFormOpen = (state: RootState) =>
  state.priorities.dialog?.type === 'NEW_TASK';

export const selectNewTaskFormPlaylist = (state: RootState) =>
  state.priorities.dialog?.type === 'NEW_TASK' ? state.priorities.dialog.playlist : null;

export const selectPrioritiesDialog = (state: RootState) => state.priorities.dialog;

export const selectSecondaryDialog = (state: RootState) => state.priorities.secondaryDialog;

export const selectNuggetToAdd = (state: RootState) => state.priorities.nuggetToAdd;

export const selectShouldAutoOpenPlaylistDrawer = (state: RootState) =>
  state.priorities.dialog?.type === 'NEW_TASK' || state.priorities.dialog?.type === 'EDIT_TASK'
    ? state.priorities.dialog.shouldAutoOpenPlaylistDrawer
    : false;
